import { ref } from 'vue'
import { toPositiveIntOr } from '../utils/contracts.js'

const toStringOrEmpty = (value) => String(value ?? '').trim()

const normalizeValue = (entry, raw) => {
  if (typeof entry.parse === 'function') {
    return entry.parse(raw)
  }

  if (entry.type === 'int') {
    return toPositiveIntOr(raw, entry.defaultValue ?? 1)
  }

  return raw ?? entry.defaultValue ?? ''
}

const serializeValue = (entry, value) => {
  if (typeof entry.serialize === 'function') {
    return entry.serialize(value)
  }

  if (value === undefined || value === null) return undefined
  const text = toStringOrEmpty(value)
  if (!text) return undefined
  return text
}

export const useQueryState = ({ route, router, entries = [] } = {}) => {
  const syncing = ref(false)
  const lastSignature = ref('')

  const hydrateFromQuery = () => {
    for (const entry of entries) {
      const queryKey = entry.queryKey
      const raw = route.query?.[queryKey]
      const nextValue = normalizeValue(entry, raw)
      entry.set(nextValue)
    }
  }

  const buildQuery = () => {
    const nextQuery = { ...route.query }
    const managedKeys = new Set(entries.map((entry) => entry.queryKey))

    for (const entry of entries) {
      const serialized = serializeValue(entry, entry.get())
      if (serialized === undefined) {
        delete nextQuery[entry.queryKey]
      } else {
        nextQuery[entry.queryKey] = serialized
      }
    }

    // stale query cleanup for managed keys with empty values.
    for (const key of managedKeys) {
      const value = nextQuery[key]
      if (value === '' || value === null || value === undefined) {
        delete nextQuery[key]
      }
    }

    return nextQuery
  }

  const syncToRoute = async () => {
    const nextQuery = buildQuery()
    const signature = JSON.stringify(nextQuery)
    // replay-safe route sync: ignore identical signatures to avoid infinite loops.
    if (signature === lastSignature.value || syncing.value) return

    syncing.value = true
    try {
      await router.replace({ query: nextQuery })
      lastSignature.value = signature
    } finally {
      syncing.value = false
    }
  }

  const resetInvalid = (defaults = {}) => {
    for (const entry of entries) {
      if (!entry.validate) continue
      const current = entry.get()
      if (entry.validate(current)) continue

      const fallback = defaults[entry.queryKey] ?? entry.defaultValue
      entry.set(fallback)
    }
  }

  return {
    syncing,
    hydrateFromQuery,
    syncToRoute,
    resetInvalid,
    buildQuery,
  }
}


